import React, { useMemo, useState } from "react";
import getUserAvatar from "../../helpers/getUserAvatar";
import getUserRank from "../../helpers/getUserRank";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Button,
  Chip,
  Divider,
  Menu,
  Stack,
  Typography,
} from "@mui/material";
import { signOut } from "../../state/actions/user";
import { trackUserProfileDropdownAction } from "../../helpers/analytics";
import useQueryPreservingNavigate from "../../helpers/useQueryPreservingNavigate";
import { useLocation } from "@reach/router";
import useGetPageByRole from "../../helpers/useGetPageByRole";

const Profile = () => {
  const email = useSelector((state) => state.user?.email);
  const allPoints = useSelector((state) => state.user?.points?.all);
  const profile = useSelector((state) => state.user.profile);
  const user = useSelector((state) => state.user);
  const roles = useSelector((state) => state.user.roles || []);
  const dispatch = useDispatch();
  const navigate = useQueryPreservingNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);

  const pageByRole = useGetPageByRole();

  const avatarSrc = useMemo(() => {
    return getUserAvatar(allPoints);
  }, [allPoints]);

  const rank = useMemo(() => {
    return getUserRank(allPoints);
  }, [allPoints]);

  const fullName = useMemo(() => {
    const firstName = profile?.firstname || "";
    const lastName = profile?.lastname || "";

    return `${firstName} ${lastName}`;
  }, [profile]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path, actionId) => {
    trackUserProfileDropdownAction(actionId);
    navigate(pageByRole);
    handleMenuClose();
  };

  const handleSignOut = () => {
    handleMenuClose();

    dispatch(signOut(user, location.search));
  };

  return (
    <>
      <Stack
        sx={{ alignItems: "center", cursor: "pointer" }}
        direction="row"
        onClick={handleMenuOpen}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            textAlign: "right",
            color: "#6a27e8",
            width: "110px",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {email}
        </Typography>

        <Avatar
          src={avatarSrc}
          sx={{
            width: "41px",
            height: "41px",
          }}
        />
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <Stack
          direction="row"
          gap="14px"
          sx={{
            py: "26px",
            pl: "14px",
            pr: "44px",
          }}
        >
          <Avatar
            src={avatarSrc}
            sx={{
              width: "41px",
              height: "41px",
            }}
          />

          <Stack>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#2b2a28",
              }}
            >
              {fullName}
            </Typography>

            <Typography
              sx={{
                fontSize: "13px",
                lineHeight: "166%",
                color: "#6a27e8",
                marginBottom: "8px",
              }}
            >
              {rank}
            </Typography>

            <Stack direction="row" gap={1} sx={{ mb: "8px" }}>
              {roles.map((role) => (
                <Chip
                  key={`role-${role}`}
                  label={role}
                  size="small"
                  sx={{
                    color: "#A2AAB5",
                  }}
                />
              ))}
            </Stack>

            <Typography
              sx={{
                fontSize: "12px",
                color: "#A2AAB5",
              }}
            >
              {email}
            </Typography>

            <Divider sx={{ my: "14px" }}></Divider>

            <Stack gap="4px">
              <Button
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: "normal",
                  justifyContent: "flex-start",
                  backgroundColor: "#ffffff",
                  color: "#2b2a28",
                  border: "none",
                  textTransform: "none",
                  width: "100%",
                  borderRadius: "16px",
                  "&:hover": {
                    border: "none",
                    background: "#6a27e8",
                    color: "#ffffff",
                  },
                }}
                onClick={() => handleNavigation("settings", "Profile Settings")}
              >
                Profile Settings
              </Button>

              <Button
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: "normal",
                  justifyContent: "flex-start",
                  backgroundColor: "#ffffff",
                  color: "#2b2a28",
                  border: "none",
                  textTransform: "none",
                  width: "100%",
                  borderRadius: "16px",
                  "&:hover": {
                    border: "none",
                    background: "#6a27e8",
                    color: "#ffffff",
                  },
                }}
                onClick={() => handleNavigation("promo", "Promote Survey")}
              >
                Promote Survey
              </Button>

              <Button
                variant="outlined"
                color="primary"
                sx={{
                  color: "#6a27e8",
                  fontWeight: "800",
                  justifyContent: "flex-start",
                  backgroundColor: "#ffffff",
                  border: "none",
                  textTransform: "none",
                  width: "100%",
                  borderRadius: "16px",
                  "&:hover": {
                    border: "none",
                    background: "#6a27e8",
                    color: "#ffffff",
                  },
                }}
                onClick={handleSignOut}
              >
                Sign out
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Menu>
    </>
  );
};

export default Profile;
